import React from 'react';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import type { RailPhrase, RailView } from '../../turncard/rail';
import AudioPlayer from '../ui/AudioPlayer';
import styles from './Rail.module.css';

const STATE_TEXT = {
  done: INTERFACE_TEXT.tsPartDone,
  current: INTERFACE_TEXT.tsPartCurrent,
  todo: INTERFACE_TEXT.tsPartTodo,
} as const;

const Phrase: React.FC<{ phrase: RailPhrase; disabled: boolean }> = ({
  phrase,
  disabled,
}) => {
  const label = phrase.targetText ?? phrase.gloss ?? '';
  return (
    <li className={styles.phrase}>
      <span className={styles.words}>
        {phrase.targetText && (
          <span className={styles.el}>{phrase.targetText}</span>
        )}
        {phrase.gloss && <span className={styles.gloss}>{phrase.gloss}</span>}
      </span>
      <div
        role="group"
        aria-label={`${INTERFACE_TEXT.rkReplayPrefix} ${label}`}
      >
        <AudioPlayer audioUrl={phrase.audioUrl} disabled={disabled} />
      </div>
    </li>
  );
};

/**
 * The left rail of the wide layout (#307): the parts, the phrases learned so far (each replayable
 * through the same player as the card, BR-009) and the keyboard hints. The caller shows it only
 * when the container is wide enough; a check-in rail has parts only (BR-043, BR-048).
 */
export const Rail: React.FC<{
  rail: RailView;
  /** The learner is recording: replay waits so the clip doesn't play into the mic. */
  disabled: boolean;
}> = ({ rail, disabled }) => (
  <aside
    className={styles.rail}
    aria-label={INTERFACE_TEXT.rkRailLabel}
    data-testid="turncard-rail"
  >
    {rail.parts.length > 0 && (
      <ol className={styles.parts}>
        {rail.parts.map((p) => (
          <li
            key={p.id}
            className={`${styles.part} ${styles[p.state]}`}
            aria-current={p.state === 'current' ? 'step' : undefined}
            data-state={p.state}
          >
            <b>{p.label}</b>
            <span className={styles.sr}>{`, ${STATE_TEXT[p.state]}`}</span>
            {p.phrases.length > 0 && (
              <ul className={styles.phrases}>
                {p.phrases.map((ph) => (
                  <Phrase key={ph.id} phrase={ph} disabled={disabled} />
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    )}
    {rail.loose.length > 0 && (
      <section aria-labelledby="rail-learned">
        <h3 id="rail-learned" className={styles.heading}>
          {INTERFACE_TEXT.rkLearnedHeading}
        </h3>
        <ul className={styles.phrases}>
          {rail.loose.map((ph) => (
            <Phrase key={ph.id} phrase={ph} disabled={disabled} />
          ))}
        </ul>
      </section>
    )}
    <dl className={styles.keys} aria-label={INTERFACE_TEXT.rkKeysHeading}>
      <div>
        <dt>
          <kbd>Space</kbd>
        </dt>
        <dd>{INTERFACE_TEXT.rkKeySpeak}</dd>
      </div>
      <div>
        <dt>
          <kbd>R</kbd>
        </dt>
        <dd>{INTERFACE_TEXT.rkKeyAgain}</dd>
      </div>
      <div>
        <dt>
          <kbd>S</kbd>
        </dt>
        <dd>{INTERFACE_TEXT.rkKeySlower}</dd>
      </div>
      <div>
        <dt>
          <kbd>Esc</kbd>
        </dt>
        <dd>{INTERFACE_TEXT.rkKeyPause}</dd>
      </div>
    </dl>
  </aside>
);

export default Rail;
